import type { Clause, ClauseOp, PatternBased, PatternBranch, RuleAction, RuleBased } from '@kkp-fraud/shared';
import { branchesOf, isExpressionRow, needsPatternBranches, patternBranchesOf } from './branches';
import { expressionRowText } from './expressions';
import { t } from './i18n';

/**
 * Turning a rule document into a sentence a fraud analyst can read without knowing JSON.
 * Everything here is pure — `format.test.ts` covers every operator, list, range and chain.
 */

const OP_SYMBOL: Record<ClauseOp, string> = {
  eq: '=',
  neq: '≠',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  in: '∈',
  nin: '∉',
  between: 'between',
  // Only the fallback below would read these: a set clause is worded by `fmt.in_set`.
  in_set: 'in_set',
  not_in_set: 'not_in_set',
};

/** A scalar as it appears in a condition: strings quoted, numbers bare, booleans bare. */
export function formatValue(v: unknown): string {
  if (v === null) return 'null';
  if (v === undefined) return '∅';
  if (typeof v === 'string') return `'${v}'`;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return `[${v.map(formatValue).join(', ')}]`;
  return JSON.stringify(v);
}

/** One clause without its leading AND / OR. */
export function formatClause(clause: Clause): string {
  const field = String(clause.field);
  const { op, value } = clause;

  if (op === 'between') {
    const [min, max] = Array.isArray(value) ? value : [undefined, undefined];
    return t('fmt.between', { field, min: formatValue(min), max: formatValue(max) });
  }
  if (op === 'in' || op === 'nin') {
    const list = Array.isArray(value) ? value : [];
    return `${field} ${OP_SYMBOL[op]} [${list.map(formatValue).join(', ')}]`;
  }
  if (op === 'in_set' || op === 'not_in_set') {
    // The list id bare — a name, as the engine's evidence prints it — never quoted as a string.
    const list = typeof value === 'string' && value !== '' ? value : formatValue(value === '' ? undefined : value);
    return t(`fmt.${op}`, { field, list });
  }
  const symbol = OP_SYMBOL[op] ?? String(op);
  return `${field} ${symbol} ${formatValue(value)}`;
}

/** A clause list as one line: "a > 1 AND b ∉ [2, 3] OR c = true". Logic on clause 0 is ignored. */
export function formatClauses(clauses: readonly Clause[] | undefined): string {
  if (!clauses || clauses.length === 0) return t('fmt.empty');
  return clauses
    .map((c, i) => (i === 0 ? formatClause(c) : `${c.logic ?? 'AND'} ${formatClause(c)}`))
    .join(' ');
}

/**
 * 600 → "10 min", 3600 → "1 h", 5400 → "1 h 30 min", 45 → "45 s". A window longer than a day
 * in whole days (Wave 7 long windows, counted by Bangkok day) reads in days: 2592000 → "30 days",
 * 365 days → "1 year"; 86400 stays "24 h".
 */
export function formatWindow(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return t('dur.second.short', { n: seconds });
  if (seconds > 86_400 && seconds % 86_400 === 0) {
    const days = seconds / 86_400;
    return days === 365 ? t('dur.year.one') : t('dur.day.short', { n: days });
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round(seconds % 60);
  const parts: string[] = [];
  if (h) parts.push(t('dur.hour.short', { n: h }));
  if (m) parts.push(t('dur.minute.short', { n: m }));
  if (s) parts.push(t('dur.second.short', { n: s }));
  return parts.join(' ') || t('dur.second.short', { n: 0 });
}

/** "count of approved per card_id ≥ 3 in 10 min where mcc ∈ [7995, 6051, 5816]" */
export function formatPatternCondition(
  rule: Pick<PatternBased, 'scope' | 'event' | 'aggregate' | 'aggregateField' | 'windowSeconds' | 'filters'> & { threshold?: number },
): string {
  const agg =
    rule.aggregate === 'count'
      ? 'count'
      : `${rule.aggregate}(${String(rule.aggregateField ?? '?')})`;
  const head = t('fmt.pb.head', { agg, event: rule.event, scope: rule.scope, threshold: rule.threshold ?? '?', window: formatWindow(rule.windowSeconds) });
  const filters = rule.filters ?? [];
  if (filters.length === 0) return head;
  return t('fmt.pb.where', { head, filters: formatClauses(filters) });
}

/**
 * One pattern action row: its measures joined by the row's AND/OR. A row of one measure is
 * exactly `formatPatternCondition` of that measure.
 */
export function formatPatternRow(row: PatternBranch): string {
  // 2a expressions — an expression row reads as its conditions over its named counters
  if (isExpressionRow(row)) return expressionRowText(row);
  return row.measures.map((m) => formatPatternCondition(m)).join(` ${row.logic ?? 'AND'} `);
}

/** The one-line condition for either rule family (row 1 — the row the engine evaluates). */
export function formatRuleCondition(rule: RuleBased | PatternBased): string {
  if ('clauses' in rule) return formatClauses(rule.clauses);
  const first = patternBranchesOf(rule)[0];
  return first && (first.measures.length > 1 || isExpressionRow(first)) ? formatPatternRow(first) : formatPatternCondition(rule);
}

/**
 * One condition line per action row, for the rule table. A rule with one row is one line
 * with no action prefix — exactly `formatRuleCondition`. A pattern rule's line is that row's
 * own measures (scope, event, aggregate, window, threshold, filters each), joined by AND/OR.
 */
export function formatBranches(rule: RuleBased | PatternBased): { action: RuleAction | null; condition: string }[] {
  if (!('clauses' in rule)) {
    const rows = patternBranchesOf(rule);
    if (!needsPatternBranches(rows)) return [{ action: null, condition: formatPatternCondition(rule) }];
    if (rows.length === 1) return [{ action: null, condition: formatPatternRow(rows[0]!) }];
    return rows.map((row) => ({ action: row.action, condition: formatPatternRow(row) }));
  }
  const rows = branchesOf(rule);
  if (rows.length <= 1) return [{ action: null, condition: formatClauses(rule.clauses) }];
  return rows.map((row) => ({ action: row.action, condition: formatClauses(row.clauses) }));
}

/** "requires fraud_score" — the note that explains why a rule can be skipped. */
export function formatRequires(requires: readonly string[] | undefined): string {
  if (!requires || requires.length === 0) return '';
  return `requires ${requires.join(', ')}`;
}

// ---------------------------------------------------------------------------
// Presentation helpers used across the tabs
// ---------------------------------------------------------------------------

const NUM = new Intl.NumberFormat('en-GB');

export function num(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return digits > 0
    ? n.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits })
    : NUM.format(Math.round(n));
}

export function ms(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return `${n.toFixed(n < 10 ? 2 : 1)} ms`;
}

/** Tail of an identifier — enough to recognise a card or transaction, not enough to leak one. */
export function shortId(id: string | null | undefined, keep = 8): string {
  if (!id) return '—';
  return id.length <= keep ? id : `…${id.slice(-keep)}`;
}

/**
 * How many trailing characters of a card id the console shows — the same everywhere, lists and
 * case detail alike (UX review P2-13): the last four, as on a card statement.
 */
export const CARD_KEEP = 4;

/** A card id in its one short form (`…2468`); the full value goes in the tooltip (`CardLink` / `CardId`). */
export function shortCard(cardId: string | null | undefined): string {
  return shortId(cardId, CARD_KEEP);
}

// ---------------------------------------------------------------------------
// Dates and times — ONE formatter for the console (UX review P2-7, lead's decision 30 Sep)
// ---------------------------------------------------------------------------

/**
 * The console's users are in Thailand, so every time a person reads is Thai local time,
 * whatever the browser's own zone: `dd/MM/yyyy HH:mm:ss`. The exact UTC instant goes in the
 * element's `title` (`<When>` in components/When.tsx), and a column header says "Thai time"
 * once. The API keeps ISO-8601 UTC; the Report's CSV time column is Thai time, exactly as
 * the screen shows it, and its header names the zone (QA-M-09).
 */
export const CONSOLE_TIME_ZONE = 'Asia/Bangkok';

/**
 * Asia/Bangkok is UTC+7 all year (no daylight saving since 1920), so a Thai calendar day
 * starts at 17:00 UTC the day before. Fixed, like `CONSOLE_TIME_ZONE` it belongs to.
 */
const BANGKOK_OFFSET_MS = 7 * 3_600_000;

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A `YYYY-MM-DD` Thai calendar day that really exists (no 2026-02-30); `null` otherwise. */
export function parseThaiDay(day: string): { y: number; m: number; d: number } | null {
  const match = day.match(YMD);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) return null;
  return { y, m, d };
}

/** The instant a Thai calendar day starts (00:00 in Bangkok), whatever the browser's zone. */
export function thaiMidnight(y: number, m: number, d: number): Date {
  // Date.UTC normalises overflow (day 0 = the last day of the month before, month 13 = January).
  return new Date(Date.UTC(y, m - 1, d) - BANGKOK_OFFSET_MS);
}

const BANGKOK = new Intl.DateTimeFormat('en-GB', {
  timeZone: CONSOLE_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

function parseInstant(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Bangkok wall-clock fields of an instant. */
function bangkokParts(d: Date): { y: string; mo: string; d: string; h: string; mi: string; s: string } {
  const parts = BANGKOK.formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return { y: get('year'), mo: get('month'), d: get('day'), h: get('hour'), mi: get('minute'), s: get('second') };
}

/** `29/09/2026 17:06:05` — Thai local time. `—` for nothing; an unparsable value is shown as sent. */
export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = parseInstant(iso);
  if (!d) return String(iso);
  const p = bangkokParts(d);
  return `${p.d}/${p.mo}/${p.y} ${p.h}:${p.mi}:${p.s}`;
}

/** `17:06:05` — Thai local time of day. */
export function timeOfDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = parseInstant(iso);
  if (!d) return String(iso);
  const p = bangkokParts(d);
  return `${p.h}:${p.mi}:${p.s}`;
}

/** `2026-09-29` — the Thai calendar day an instant falls on (for "is it today?", a report period). */
export function thaiDay(iso: string | Date): string {
  const d = typeof iso === 'string' ? parseInstant(iso) : iso;
  if (!d) return '';
  const p = bangkokParts(d);
  return `${p.y}-${p.mo}-${p.d}`;
}

/**
 * An actor as a person reads it: the admin service writes `upn (id)` — `admin@kkp-poc.local
 * (u-admin)` — and the id in brackets is for the audit, not for the screen (the full string
 * stays in the tooltip where this is shown).
 */
export function actorLabel(actor: string | null | undefined): string {
  if (!actor) return '—';
  return actor.replace(/\s*\([^()]*\)\s*$/, '') || actor;
}

/** The exact instant in UTC (`2026-09-29T10:06:05.000Z`) — the tooltip under every shown time. */
export function utcIso(iso: string | null | undefined): string | undefined {
  const d = parseInstant(iso);
  return d ? d.toISOString() : undefined;
}
